'use client';

import React, { memo, useCallback, useMemo } from 'react';
import { motion, Variants, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { Button, Badge, Text, Heading } from '@/components/ui/design-system';
import Link from 'next/link';

// --- Animation Variants - Memoized for performance ---
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
} as const;

const wordVariants: Variants = {
  hidden: { opacity: 0, y: 20, rotateX: -30, transformOrigin: 'bottom' },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { type: 'spring', damping: 12, stiffness: 100 },
  },
} as const;

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
} as const;

/**
 * Renders an animated heading with kinetic typography effects.
 * @param {object} props - The component props.
 * @param {string} props.text - The text to display in the heading.
 */
const KineticHeading = memo(({ text }: { text: string }) => {
  const shouldReduceMotion = useReducedMotion();
  const words = useMemo(() => text.split(' '), [text]);

  if (shouldReduceMotion) {
    return (
      <Heading level={1} align="left" className="mb-3 text-5xl" style="gradient">
        {text}
      </Heading>
    );
  }

  return (
    <Heading level={1} align="left" className="mb-3 text-5xl" style="gradient">
      <span className="sr-only">{text}</span>
      <motion.span
        aria-hidden
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {words.map((word, index) => (
          <motion.span key={`${word}-${index}`} className="inline-block mr-4" variants={wordVariants}>
            {word}
          </motion.span>
        ))}
      </motion.span>
    </Heading>
  );
});

/**
 * Hero Section Component
 * 
 * Displays the hero section of the contact page with proper semantic HTML structure,
 * accessibility features, and responsive design following design system patterns.
 * 
 * @returns {JSX.Element} Optimized hero section with a11y and SEO compliance
 */
const ContactHero = memo(() => {
  const shouldReduceMotion = useReducedMotion();

  // Handle smooth scrolling to contact form with accessibility support
  const handleContactClick = useCallback(() => {
    const element = document.querySelector('#contact-form');
    
    if (element) {
      // Use smooth scrolling (browser will respect user preferences for reduced motion)
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Focus management for accessibility
      setTimeout(() => {
        const firstInput = element.querySelector('input:first-of-type') as HTMLElement;
        if (firstInput) {
          firstInput.focus();
          // Announce to screen readers
          const announcement = document.createElement('div');
          announcement.setAttribute('aria-live', 'polite');
          announcement.setAttribute('aria-atomic', 'true');
          announcement.className = 'sr-only';
          announcement.textContent = 'Contact form is now in focus';
          document.body.appendChild(announcement);
          setTimeout(() => document.body.removeChild(announcement), 1000);
        }
      }, 300);
    }
  }, []);

  // Handle smooth scrolling to products section
  const handleProductsClick = useCallback(() => {
    const element = document.querySelector('#featured-products');
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, []);

  // Memoized structured data for SEO - prevents recreation on every render
  const structuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Contact PAVISHA for Premium Packaging Solutions",
    "description": "Get in touch for premium BPA-free fridge bottles, leak-proof water bottles, and food-safe PET packaging solutions.",
    "mainEntity": {
      "@type": "Organization",
      "name": "PAVISHA",
      "description": "Premium BPA-free packaging solutions provider",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Customer Service",
        "availableLanguage": ["English", "Hindi"]
      }
    }
  }), []);

  return (
    <section 
      className="relative h-screen w-full overflow-hidden bg-black"
      aria-labelledby="contact-hero-heading"
      role="banner"
    >
      {/* Structured data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      {/* Skip link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Background Image */}
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <div className="relative h-full">
          {/* Desktop Image */}
          <Image
            src="/images/pages/contact/hero/hero-banner.jpeg"
            alt="Premium PAVISHA bottles and packaging solutions showcasing BPA-free fridge bottles and food-safe containers"
            fill
            priority
            sizes="(min-width: 1024px) 100vw, 0px"
            fetchPriority="high"
            className="hidden lg:block object-cover"
            quality={90}
            placeholder="blur"
            blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R+Rw="
            role="presentation"
          />
          {/* Mobile Image */}
          <Image
            src="/images/pages/contact/hero/hero-banner-mobile.jpeg"
            alt="Premium PAVISHA bottles and packaging solutions showcasing BPA-free fridge bottles and food-safe containers"
            fill
            priority
            sizes="(max-width: 1023px) 100vw, 0px"
            fetchPriority="high"
            className="lg:hidden object-cover"
            quality={90}
            placeholder="blur"
            blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R+Rw="
            role="presentation"
          />
          <div
            className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent dark:from-black/50 dark:to-transparent pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Mobile/Tablet Content overlay - Split Layout */}
      <div className="absolute inset-0 z-10 p-6 pt-24 sm:p-12 md:p-16 lg:hidden pointer-events-none">
        <div className="w-full h-full flex flex-col justify-between">
          {/* Top Content: Badge + Title (Mobile/Tablet only) */}
          <div className="flex-shrink-0">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="mb-4"
            >
              <Badge variant="premium" size="lg" radius="full" className="pointer-events-auto">
                Premium BPA-free Bottles
              </Badge>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <h1 
                id="contact-hero-heading"
                className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white max-w-2xl"
                tabIndex={-1}
              >
                Contact Us for Premium Packaging
              </h1>
            </motion.div>
          </div>

          {/* Bottom Content: CTA Buttons (Mobile/Tablet only) */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 pointer-events-auto flex-shrink-0"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={handleContactClick}
              className="group relative w-full whitespace-nowrap overflow-hidden rounded-full"
              aria-describedby="contact-btn-desc"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transition-transform duration-500 ease-in-out -translate-x-full group-hover:translate-x-0" />
              <span className="relative">Get Quote Now</span>
              <motion.div
                className="relative ml-2"
                whileHover={shouldReduceMotion ? undefined : { x: 4 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <ArrowRight className="h-4 w-4" />
              </motion.div>
            </Button>
            <span id="contact-btn-desc" className="sr-only">
              Navigate to contact form to request a quote
            </span>
            
            <Button
              variant="secondary"
              size="lg"
              onClick={handleProductsClick}
              className="w-full whitespace-nowrap rounded-full"
              aria-describedby="products-btn-desc"
            >
              View Products
            </Button>
            <span id="products-btn-desc" className="sr-only">
              Navigate to featured products section
            </span>
          </motion.div>
        </div>
      </div>

      {/* Desktop Content - Original Layout (Completely Untouched) */}
      <div className="hidden lg:block absolute inset-0 z-10 grid grid-cols-1 lg:grid-cols-12 items-center py-8 sm:py-12 md:py-20 lg:py-32 px-8 sm:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="lg:col-span-6 w-full max-w-xl text-left text-white"
        >
          <motion.div variants={itemVariants}>
            <Badge variant="premium" size="lg" radius="full" className="mb-4">
              Premium BPA-free Bottles
            </Badge>
          </motion.div>

          <h1 
            id="contact-hero-heading-desktop"
            className="mb-3 text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white"
            tabIndex={-1} /* Allow focus for screen readers but not in tab order */
          >
            Contact Us for Premium Packaging
          </h1>

          <motion.div variants={itemVariants}>
            <Text
              variant="body-large"
              className="text-sm sm:text-base md:text-lg xl:text-xl mb-6 sm:mb-8 max-w-xl font-normal leading-relaxed text-gray-700 dark:text-gray-100"
            >
              Get in touch for premium BPA-free fridge bottles, leak-proof water bottles, and food-safe PET packaging solutions. We serve businesses with custom packaging needs.
            </Text>
          </motion.div>

          {/* CTA buttons with original structure and enhanced micro-interactions */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4 sm:mt-6"
          >
            <Button
              variant="primary"
              size="lg"
              onClick={handleContactClick}
              className="group relative w-full sm:w-auto whitespace-nowrap overflow-hidden rounded-full"
              aria-describedby="contact-btn-desc-desktop"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transition-transform duration-500 ease-in-out -translate-x-full group-hover:translate-x-0" />
              <span className="relative">Get Quote Now</span>
              <motion.div
                className="relative ml-2"
                whileHover={shouldReduceMotion ? undefined : { x: 4 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <ArrowRight className="h-4 w-4" />
              </motion.div>
            </Button>
            <span id="contact-btn-desc-desktop" className="sr-only">
              Navigate to contact form to request a quote
            </span>
            
            <Button
              variant="secondary"
              size="lg"
              onClick={handleProductsClick}
              className="w-full sm:w-auto whitespace-nowrap rounded-full"
              aria-describedby="products-btn-desc-desktop"
            >
              View Products
            </Button>
            <span id="products-btn-desc-desktop" className="sr-only">
              Navigate to featured products section
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
});

const MemoizedContactHero = memo(ContactHero);
MemoizedContactHero.displayName = 'ContactHero';
export default MemoizedContactHero;
